import { LanguagesEnum } from '@/api/enums';
import { File } from '@/client/files';
import { isMobile } from '@/utils/helpers/ui.helper';
import i18next, { t } from 'i18next';
import 'pdfjs-dist/build/pdf.worker.min.mjs';
import { Checkbox } from 'primereact/checkbox';
import React, { useEffect, useId, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import styled from 'styled-components';
import { AppButton } from '../buttons';
import { FlexContainer } from '../styled-ui';

const StyledFlexContainer = styled(FlexContainer)`
  position: relative;
  width: 100%;
  padding-top: var(--default-padding);

  .resizer {
    position: absolute;
    height: 100%;
  }

  .invisible {
    visibility: hidden;
  }
`;

const StyledBox = styled(FlexContainer)`
  background-color: var(--beige-main);
  border-top: 1px solid var(--gray-dark);
  padding: var(--default-padding);
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 999;
  height: 100px;
  border-radius: 0 0 var(--default-border-radius) var(--default-border-radius);

  > div {
    max-width: 1000px;
    display: flex;
    justify-content: flex-end;
  }

  .nav {
    border-radius: 50%;
    width: 48px;
    height: 48px;
  }
`;

type ScormPDFVieweProps = {
  file: File | null;
  hasPreviousModules?: boolean;
  hideNextButton?: boolean;
  continueOrFinishButton?: React.ReactNode;
  requiresReadConfirmation?: boolean;
  hasConfirmedRead?: boolean;
  attemptLanguage?: LanguagesEnum;
  onPreviousModule?: () => void;
  onLastPageReached?: () => void;
  onReadConfirmationChange?: (confirmed: boolean) => void;
};

export const ContentPDFViewer: React.FC<ScormPDFVieweProps> = ({
  file,
  hasPreviousModules = false,
  hideNextButton,
  continueOrFinishButton,
  requiresReadConfirmation,
  hasConfirmedRead,
  attemptLanguage,
  onPreviousModule,
  onLastPageReached,
  onReadConfirmationChange,
}) => {
  const readConfirmationInputId = useId();
  const readConfirmationLng = attemptLanguage ?? i18next.language;
  const [isDocumentLoaded, setIsDocumentLoaded] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasViewedLastPage, setHasViewedLastPage] = useState<boolean>(false);

  const onDocumentLoadSuccess = ({ numPages }: any) => {
    setTotalPages(numPages);
    setIsDocumentLoaded(true);
  };

  useEffect(() => {
    if (
      isDocumentLoaded &&
      totalPages > 0 &&
      pageNumber === totalPages &&
      onLastPageReached
    ) {
      setHasViewedLastPage(true);
      onLastPageReached();
    }
  }, [pageNumber, totalPages, isDocumentLoaded, onLastPageReached]);

  useEffect(() => {
    document
      .querySelector('.content')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageNumber]);

  const handleError = (e: Error) => {
    // eslint-disable-next-line no-console
    console.dir(e);
  };

  useEffect(() => {
    setPageNumber(1);
    setHasViewedLastPage(false);
  }, [file]);

  return (
    <>
      {/* PDF Document */}
      <StyledFlexContainer
        direction="row"
        justify="center"
        className="relative m-auto"
      >
        <div className="resizer" />
        <FlexContainer justify="center" width="auto">
          <Document
            file={file && file.signedUrl}
            loading={t('generic.loading')}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={handleError}
            onSourceError={handleError}
            externalLinkTarget="_blank"
          >
            <Page
              pageNumber={pageNumber}
              loading={t('generic.loading')}
              width={
                isMobile()
                  ? (document?.querySelector('.mobile')?.clientWidth || 0) - 100
                  : 1000
              }
              onLoadError={handleError}
              onRenderError={handleError}
            />
          </Document>
        </FlexContainer>
      </StyledFlexContainer>
      <StyledBox>
        <FlexContainer gap="var(--small-padding)" align="center">
          {pageNumber < totalPages || !hasViewedLastPage ? (
            <>
              <AppButton
                severity="info"
                isDisabled={
                  (pageNumber === 1 && !hasPreviousModules) || !isDocumentLoaded
                }
                type="outlined"
                icon={<span className="pi pi-arrow-left" />}
                ariaLabel={t('generic.previousPage')}
                className={`nav ${
                  (pageNumber === 1 && !hasPreviousModules) || !isDocumentLoaded
                    ? 'invisible'
                    : ''
                } ${isMobile() ? 'mobile-left' : ''}`}
                onClick={() =>
                  pageNumber === 1 && hasPreviousModules
                    ? onPreviousModule?.()
                    : setPageNumber(pageNumber - 1)
                }
              />
              <AppButton
                isDisabled={!isDocumentLoaded}
                icon={<span className="pi pi-arrow-right" />}
                ariaLabel={t('generic.nextPage')}
                className={`nav ${
                  hideNextButton && pageNumber === totalPages ? 'invisible' : ''
                } ${isMobile() ? 'mobile-right' : ''}`}
                onClick={() => {
                  if (pageNumber < totalPages) {
                    setPageNumber(pageNumber + 1);
                  }
                }}
              />
            </>
          ) : (
            <>
              {requiresReadConfirmation && (
                <FlexContainer
                  align="center"
                  gap="var(--xsmall-padding)"
                  width="auto"
                >
                  <Checkbox
                    inputId={readConfirmationInputId}
                    checked={!!hasConfirmedRead}
                    onChange={(e) => onReadConfirmationChange?.(!!e.checked)}
                  />
                  <label htmlFor={readConfirmationInputId}>
                    {t('content.pdf.readConfirmation.label', {
                      lng: readConfirmationLng,
                    })}
                  </label>
                </FlexContainer>
              )}
              <div>{continueOrFinishButton}</div>
            </>
          )}
        </FlexContainer>
      </StyledBox>
    </>
  );
};

export default ContentPDFViewer;
